import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Headphones, Send, Loader2, Clock, Mail, Phone } from "lucide-react";

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_product: "", problem: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name || !form.email || !form.message) { toast.error("Please fill name, email and message."); return; }
    setLoading(true);
    try {
      await api.post("/support", form);
      toast.success("Support request sent. Our team will respond as soon as possible.");
      setForm({ name: "", email: "", phone: "", service_product: "", problem: "", message: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <span className="text-xs font-mono uppercase tracking-widest text-cyan-400">24/7 Technical Support</span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white mt-2">How can we help?</h1>
          <p className="text-slate-400 mt-3">Report a problem with a product or service. Our Madeira specialists provide round-the-clock computer, IT and networking support. No account required.</p>
          <div className="mt-8 space-y-4">
            {[[Clock, "24/7 availability", "We respond around the clock"], [Mail, "revona.eu@gmail.com", "Email support"], [Phone, "+351 913 286 138", "Phone support"]].map(([Ic, t, d]) => (
              <div key={t} className="flex items-center gap-4 tactical-card p-4">
                <div className="w-10 h-10 rounded-lg bg-[#0A0E1A] border border-[#1E2F4D] flex items-center justify-center"><Ic className="w-5 h-5 text-cyan-400" /></div>
                <div><div className="text-white font-medium text-sm">{t}</div><div className="text-xs text-slate-400">{d}</div></div>
              </div>
            ))}
          </div>
        </div>

        <div className="tactical-card p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-6"><Headphones className="w-6 h-6 text-cyan-400" /><h2 className="font-display font-semibold text-white text-lg">Support request</h2></div>
          <div className="space-y-4">
            <F label="Name *" k="name" form={form} set={set} tid="support-name-input" />
            <F label="Email *" k="email" type="email" form={form} set={set} tid="support-email-input" />
            <F label="Phone" k="phone" form={form} set={set} tid="support-phone-input" />
            <F label="Service / Product" k="service_product" form={form} set={set} tid="support-serviceproduct-input" />
            <F label="Problem" k="problem" form={form} set={set} tid="support-problem-input" />
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Message *</label>
              <textarea value={form.message} onChange={set("message")} rows={4} data-testid="support-message-input"
                className="w-full bg-[#0A0E1A] border border-[#1E2F4D] rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#2563EB]" />
            </div>
            <button onClick={submit} disabled={loading} data-testid="support-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />} Send Request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, k, type = "text", form, set, tid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-400 mb-1.5">{label}</label>
      <input value={form[k]} onChange={set(k)} type={type} data-testid={tid}
        className="w-full bg-[#0A0E1A] border border-[#1E2F4D] rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#2563EB]" />
    </div>
  );
}
